import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save,
  Close,
  Info,
  Code,
  Preview,
  History
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { PromptVariablesDocs } from './PromptVariablesDocs';
import { API_URL } from '../../utils/config';

interface LLMModel {
  id: number;
  provider: string;
  modelName: string;
  alias?: string;
  family: string;
  release: string;
}

interface LLMPrompt {
  promptKey: string;
  modelId: number;
  model?: LLMModel;
  systemPrompt?: string;
  prompt: string;
  temperature: number;
  maxTokens: number;
  pdfCharacters: number;
  description?: string;
  isActive: boolean;
}

interface PromptEditorProps {
  open: boolean;
  onClose: () => void;
  prompt: LLMPrompt | null;
  isCreating: boolean;
}

export const PromptEditor: React.FC<PromptEditorProps> = ({
  open,
  onClose,
  prompt,
  isCreating
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<LLMPrompt>>({
    promptKey: '',
    modelId: undefined,
    systemPrompt: '',
    prompt: '',
    temperature: 0.7,
    maxTokens: 2000,
    pdfCharacters: 8000,
    description: '',
    isActive: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load initial data
  useEffect(() => {
    if (prompt) {
      setFormData({
        promptKey: prompt.promptKey,
        modelId: prompt.modelId,
        systemPrompt: prompt.systemPrompt || '',
        prompt: prompt.prompt,
        temperature: prompt.temperature,
        maxTokens: prompt.maxTokens,
        pdfCharacters: prompt.pdfCharacters,
        description: prompt.description || '',
        isActive: prompt.isActive
      });
    } else if (isCreating) {
      setFormData({
        promptKey: '',
        modelId: undefined,
        systemPrompt: '',
        prompt: '',
        temperature: 0.7,
        maxTokens: 2000,
        pdfCharacters: 8000,
        description: '',
        isActive: true
      });
    }
  }, [prompt, isCreating]);

  // Fetch available models
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['models'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/models`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch models');
      }
      return await response.json();
    }
  });

  const models = modelsData?.data?.models || [];
  const groupedModels = modelsData?.data?.grouped || {};

  // Update prompt mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<LLMPrompt>) => {
      const url = isCreating 
        ? `${API_URL}/api/marketcompass/admin/prompts/create`
        : `${API_URL}/api/marketcompass/admin/prompts/${prompt?.promptKey}/update`;
      
      const response = await authenticatedFetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        body: JSON.stringify(data)
      });
      
      if (!response || !response.ok) {
        const errorData = await response?.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save prompt');
      }
      
      return await response.json();
    },
    onSuccess: () => {
      onClose();
    },
    onError: (error: any) => {
      setErrors({
        submit: error.message || 'Failed to save prompt'
      });
    }
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (isCreating && !formData.promptKey) {
      newErrors.promptKey = 'Prompt key is required';
    }
    if (isCreating && formData.promptKey && !/^[a-zA-Z0-9_]+$/.test(formData.promptKey)) {
      newErrors.promptKey = 'Only letters, numbers, and underscores allowed';
    }
    if (!formData.modelId) {
      newErrors.modelId = 'Model selection is required';
    }
    if (!formData.prompt) {
      newErrors.prompt = 'Prompt text is required';
    }
    if (formData.temperature === undefined || formData.temperature < 0 || formData.temperature > 1) {
      newErrors.temperature = 'Temperature must be between 0 and 1';
    }
    if (!formData.maxTokens || formData.maxTokens < 1) {
      newErrors.maxTokens = 'Max tokens must be positive';
    }
    if (!formData.pdfCharacters || formData.pdfCharacters < 100) {
      newErrors.pdfCharacters = 'PDF characters must be at least 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      updateMutation.mutate(formData);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            {isCreating ? 'Create New Prompt' : `Edit Prompt: ${prompt?.promptKey}`}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Main Content Area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab icon={<Code />} label="Editor" />
              <Tab icon={<Preview />} label="Preview" disabled />
              <Tab icon={<History />} label="History" disabled />
            </Tabs>

            <Box sx={{ flex: 1, p: 2 }}>
              {activeTab === 0 && (
                <Grid container spacing={2} sx={{ height: '100%' }}>
                  {/* Prompt Key (only for new prompts) */}
                  {isCreating && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Prompt Key"
                        value={formData.promptKey}
                        onChange={(e) => setFormData({ ...formData, promptKey: e.target.value })}
                        error={!!errors.promptKey}
                        helperText={errors.promptKey || 'Unique identifier for this prompt (e.g., market_report_summary)'}
                        disabled={!isCreating}
                      />
                    </Grid>
                  )}

                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      helperText="Brief description of what this prompt does"
                    />
                  </Grid>

                  {/* System Prompt */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      System Prompt (Optional)
                    </Typography>
                    <Box sx={{ height: 120, border: 1, borderColor: 'divider', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                      <Editor
                        height="120px"
                        defaultLanguage="plaintext"
                        value={formData.systemPrompt}
                        onChange={(value: string | undefined) => setFormData({ ...formData, systemPrompt: value || '' })}
                        options={{
                          minimap: { enabled: false },
                          lineNumbers: 'off',
                          wordWrap: 'on',
                          fontSize: 14,
                          scrollBeyondLastLine: false
                        }}
                        onMount={(editor, monaco) => {
                          // Apply the same syntax highlighting for consistency
                          monaco.editor.setModelLanguage(editor.getModel()!, 'prompt-language');
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Main Prompt */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Prompt Text *
                    </Typography>
                    {errors.prompt && (
                      <Alert severity="error" sx={{ mb: 1 }}>{errors.prompt}</Alert>
                    )}
                    <Box sx={{ height: 400, border: 1, borderColor: 'divider', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                      <Editor
                        height="400px"
                        defaultLanguage="plaintext"
                        value={formData.prompt}
                        onChange={(value: string | undefined) => setFormData({ ...formData, prompt: value || '' })}
                        options={{
                          minimap: { enabled: false },
                          wordWrap: 'on',
                          fontSize: 14,
                          scrollBeyondLastLine: false,
                          automaticLayout: true
                        }}
                        onMount={(editor, monaco) => {
                          // Define custom tokenizer for template variables
                          monaco.languages.register({ id: 'prompt-language' });
                          monaco.languages.setMonarchTokensProvider('prompt-language', {
                            tokenizer: {
                              root: [
                                [/\{[a-zA-Z_][a-zA-Z0-9_]*\}/, 'template-variable']
                              ]
                            }
                          });
                          // Define theme with bold red for template variables
                          monaco.editor.defineTheme('prompt-theme', {
                            base: 'vs',
                            inherit: true,
                            rules: [
                              { token: 'template-variable', foreground: 'ff0000', fontStyle: 'bold' }
                            ],
                            colors: {}
                          });
                          // Set the language and theme
                          monaco.editor.setModelLanguage(editor.getModel()!, 'prompt-language');
                          monaco.editor.setTheme('prompt-theme');
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Template Variables */}
                  <Grid item xs={12}>
                    <PromptVariablesDocs />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>

          {/* Sidebar */}
          <Box sx={{ width: 400, borderLeft: 1, borderColor: 'divider', p: 2, overflowY: 'auto' }}>

            {/* Model Settings */}
            <Typography variant="h6" gutterBottom>
              Model Settings
            </Typography>

            {/* Model Selection */}
            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.modelId}>
              <InputLabel>Model *</InputLabel>
              <Select
                value={formData.modelId !== undefined && formData.modelId !== null ? formData.modelId : ''}
                onChange={(e) => setFormData({ ...formData, modelId: Number(e.target.value) })}
                label="Model *"
                disabled={modelsLoading}
              >
                {modelsLoading ? (
                  <MenuItem value="">Loading models...</MenuItem>
                ) : (
                  Object.entries(groupedModels).map(([provider, families]) => [
                    <MenuItem key={`header-${provider}`} disabled>
                      <Typography variant="overline">{provider}</Typography>
                    </MenuItem>,
                    ...Object.entries(families as Record<string, LLMModel[]>).map(([family, familyModels]) =>
                      familyModels.map((model) => (
                        <MenuItem key={model.id} value={model.id} sx={{ pl: 3 }}>
                          <Box>
                            <Typography variant="body2">{model.modelName}</Typography>
                            {model.alias && (
                              <Typography variant="caption" color="text.secondary">
                                Alias: {model.alias}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )
                  ])
                )}
              </Select>
              {errors.modelId && (
                <Typography variant="caption" color="error">{errors.modelId}</Typography>
              )}
            </FormControl>

            {/* Temperature */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Temperature: {formData.temperature}
              </Typography>
              <Slider
                value={formData.temperature || 0.7}
                onChange={(_, value) => setFormData({ ...formData, temperature: value as number })}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' }
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Lower = more focused, Higher = more creative
              </Typography>
            </Box>

            {/* Max Tokens */}
            <TextField
              fullWidth
              type="number"
              label="Max Tokens"
              value={formData.maxTokens}
              onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) || 0 })}
              error={!!errors.maxTokens}
              helperText={errors.maxTokens || 'Maximum response length'}
              sx={{ mb: 2 }}
            />

            {/* PDF Characters */}
            <TextField
              fullWidth
              type="number"
              label="PDF Characters to Extract"
              value={formData.pdfCharacters}
              onChange={(e) => setFormData({ ...formData, pdfCharacters: parseInt(e.target.value) || 0 })}
              error={!!errors.pdfCharacters}
              helperText={errors.pdfCharacters || 'Number of characters to extract from PDF'}
              sx={{ mb: 2 }}
            />

            {/* Active Status */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Only active prompts are used for processing
            </Typography>

            {errors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>{errors.submit}</Alert>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};